import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Beaker, Sparkles, AlertTriangle, Cpu, Activity, Flame, Zap } from "lucide-react";
import { useI18n } from "@/lib/i18n";

// ----- Predictive formulas (based on published academic trends, e.g. Schartel 2010,
// Lewin 2003 for flame retardants; Pielichowska 2014 for graphite-polymer composites) -----
function predict(base, params) {
  const { graphite, flameRet, fiber, densityDelta } = params;
  const dF = 1 + densityDelta / 100; // density multiplier (1.0 = base)

  // Thermal conductivity: graphite & higher density reduce, FR slightly increase
  const lambda = base.lambda * (1 - 0.015 * graphite) * (1 - 0.002 * (dF - 1) * 100) * (1 + 0.004 * flameRet);

  // LOI: FR strongly increases LOI; graphite mild + (radical scavenger); fiber neutral
  const loi = Math.min(100, base.loi + 0.55 * flameRet + 0.10 * graphite - 0.05 * fiber);

  // UL94 derived from LOI bands (per published correlation, Wilkie 2010)
  let ul94 = "HB";
  if (loi >= 32) ul94 = "V-0";
  else if (loi >= 28) ul94 = "V-1";
  else if (loi >= 25) ul94 = "V-2";
  else ul94 = "HB";

  // pHRR: FR & graphite reduce, fiber neutral; density mild
  const pHRR = Math.max(0, base.pHRR * (1 - 0.025 * flameRet) * (1 - 0.018 * graphite) * (1 + 0.003 * (dF - 1) * 100));

  // Mechanical: density up + fiber up = stronger; FR & graphite slightly weaker
  const flexural = base.flexural * dF * (1 + 0.04 * fiber) * (1 - 0.012 * flameRet) * (1 + 0.003 * graphite);
  const tensile = base.tensile * dF * (1 + 0.06 * fiber) * (1 - 0.018 * flameRet) * (1 + 0.002 * graphite);
  const compressive = base.compressive * Math.pow(dF, 1.4) * (1 + 0.02 * fiber);
  const eModulus = base.eModulus * Math.pow(dF, 1.2) * (1 + 0.05 * fiber);

  // Composite scores (0-100) for visualization
  const thermalScore = Math.max(0, Math.min(100, 100 * (1 - lambda / 0.10)));     // lower λ → better
  const fireScore = Math.max(0, Math.min(100, loi * 1.5 + (ul94 === "V-0" ? 25 : ul94 === "V-1" ? 18 : ul94 === "V-2" ? 10 : 0)));
  const mechanicalScore = Math.max(0, Math.min(100, (flexural / 250) * 60 + (tensile / 150) * 40));

  return {
    lambda: Number(lambda.toFixed(4)),
    loi: Number(loi.toFixed(1)),
    ul94,
    pHRR: Math.round(pHRR),
    flexural: Math.round(flexural),
    tensile: Math.round(tensile),
    compressive: Math.round(compressive),
    eModulus: Number(eModulus.toFixed(2)),
    thermalScore: Math.round(thermalScore),
    fireScore: Math.round(fireScore),
    mechanicalScore: Math.round(mechanicalScore),
  };
}

function ratingColor(ul94) {
  if (ul94 === "V-0") return "#39FF14";
  if (ul94 === "V-1") return "#7CFF14";
  if (ul94 === "V-2") return "#FFCC00";
  return "#FF8C00";
}

const PRESETS = [
  { id: "balanced", nameKey: "rd.sim.preset.balanced", nameEn: "Balanced",   params: { graphite: 10, flameRet: 8,  fiber: 5,  densityDelta: 15 } },
  { id: "thermal",  nameKey: "rd.sim.preset.thermal",  nameEn: "Max Thermal", params: { graphite: 18, flameRet: 0,  fiber: 0,  densityDelta: -10 } },
  { id: "fire",     nameKey: "rd.sim.preset.fire",     nameEn: "Max Fire",    params: { graphite: 4,  flameRet: 25, fiber: 0,  densityDelta: 20 } },
  { id: "mech",     nameKey: "rd.sim.preset.mech",     nameEn: "Max Mechanical", params: { graphite: 6,  flameRet: 5,  fiber: 12, densityDelta: 30 } },
];

function Bar({ value, label, color = "#00E5FF" }) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between font-mono text-[10px]">
        <span className="text-[#8B8B95] uppercase tracking-wider">{label}</span>
        <span style={{ color }}>{value}/100</span>
      </div>
      <div className="h-2 bg-[#0A0A0A] border border-[#1F1F1F] overflow-hidden">
        <div className="h-full transition-all duration-300" style={{ width: `${value}%`, background: color }}/>
      </div>
    </div>
  );
}

function ComparePair({ label, base, pred, unit, betterIsLower = false, accent }) {
  const { t } = useI18n();
  const baseNum = typeof base === "number" ? base : parseFloat(base);
  const predNum = typeof pred === "number" ? pred : parseFloat(pred);
  const isNumeric = !isNaN(baseNum) && !isNaN(predNum);
  const delta = isNumeric ? predNum - baseNum : 0;
  const sameStr = !isNumeric && String(base) === String(pred);
  const arrow = isNumeric
    ? (delta > 0 ? "↑" : delta < 0 ? "↓" : "=")
    : (sameStr ? "=" : "→");
  const dStr = isNumeric
    ? ((delta > 0 ? "+" : "") + delta.toFixed(Math.abs(delta) < 1 ? 3 : 1))
    : (sameStr ? t("rd.sim.unchanged", "unchanged") : String(pred));
  return (
    <div className="grid grid-cols-12 items-center gap-2 px-3 py-2 border-b border-[#1F1F1F]">
      <div className="col-span-4 tech-label rd-data-text" style={{ fontWeight: 700, opacity: 1 }}>{label}</div>
      <div className="col-span-3 font-mono text-sm rd-data-text" style={{ fontWeight: 700, opacity: 1 }}>{base}</div>
      <div className="col-span-3 font-mono text-sm rd-data-text" style={{ fontWeight: 800, opacity: 1 }}>{pred} {unit}</div>
      <div className="col-span-2 font-mono text-[11px] text-right rd-data-text" style={{ fontWeight: 700, opacity: 1 }}>
        {arrow} {dStr}
      </div>
    </div>
  );
}

export default function RDSimulationPanel({ selected }) {
  const { t } = useI18n();
  const [params, setParams] = useState({ graphite: 6, flameRet: 5, fiber: 3, densityDelta: 10 });

  const base = useMemo(() => {
    if (!selected) return null;
    return {
      lambda: selected.lambda_value,
      loi: selected.fire.loi_percent,
      ul94: selected.fire.ul94_rating,
      pHRR: selected.fire.cone_calorimeter.peak_hrr_kWm2,
      flexural: selected.mechanical.flexural_strength_kPa,
      tensile: selected.mechanical.tensile_strength_kPa,
      compressive: selected.mechanical.compressive_strength_kPa,
      eModulus: selected.mechanical.elastic_modulus_MPa,
    };
  }, [selected]);

  const pred = useMemo(() => base ? predict(base, params) : null, [base, params]);

  if (!selected || !base || !pred) return null;

  return (
    <section className="rd-sim-panel" data-testid="rd-simulation-panel">
      <header className="rd-sim-header bg-[var(--neon-green)] text-[#0A0A0A] px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4"/>
          <span className="font-mono text-xs uppercase tracking-[0.2em] font-bold">
            {t("rd.sim.title", "R&D Simulation Panel · R&D Material Design Engine")}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider">PREDICTIVE MODEL</span>
      </header>

      <div className="p-4 space-y-4">
        <p className="rd-sim-desc font-mono text-[11px] leading-relaxed">
          {t("rd.sim.desc", "Parameter simulation based on academic literature models. Adjust the additive ratios · thermal, fire and mechanical predictions update in real time.")}
        </p>

        {/* Preset buttons */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {PRESETS.map((p) => (
            <Button key={p.id} onClick={() => setParams(p.params)}
              className="rounded-none h-9 font-mono text-[10px] uppercase tracking-wider"
              style={{ backgroundColor: '#22C55E', color: '#FFFFFF', WebkitTextFillColor: '#FFFFFF', border: '2px solid #166534', fontWeight: 700, opacity: 1 }}
              data-testid={`preset-${p.id}`}>
              <Sparkles className="w-3 h-3 mr-1" style={{ color: '#FFFFFF' }}/> {t(p.nameKey, p.nameEn)}
            </Button>
          ))}
        </div>

        {/* Sliders */}
        <div className="rd-sim-card p-4 space-y-5" data-testid="sliders-panel">
          <SliderRow
            label={t("rd.sim.slider.graphite", "Graphite / Carbon Additive (%)")}
            value={params.graphite} min={0} max={20} step={0.5}
            onChange={(v) => setParams((p) => ({ ...p, graphite: v }))}
            help={t("rd.sim.slider.graphite.help", "↓ λ value · Mild radical-scavenging effect")}
            color="#00E5FF"
            testId="slider-graphite"
          />
          <SliderRow
            label={t("rd.sim.slider.fr", "Flame Retardant (%)")}
            value={params.flameRet} min={0} max={30} step={0.5}
            onChange={(v) => setParams((p) => ({ ...p, flameRet: v }))}
            help={t("rd.sim.slider.fr.help", "↑ LOI, ↓ pHRR · ↓ Mechanical strength")}
            color="#FF3B30"
            testId="slider-fr"
          />
          <SliderRow
            label={t("rd.sim.slider.fiber", "Fibre Reinforcement (%)")}
            value={params.fiber} min={0} max={15} step={0.5}
            onChange={(v) => setParams((p) => ({ ...p, fiber: v }))}
            help={t("rd.sim.slider.fiber.help", "↑ Tensile & flexural · Fire impact neutral")}
            color="#39FF14"
            testId="slider-fiber"
          />
          <SliderRow
            label={t("rd.sim.slider.density", "Density Variation (%)")}
            value={params.densityDelta} min={-30} max={50} step={1}
            onChange={(v) => setParams((p) => ({ ...p, densityDelta: v }))}
            help={t("rd.sim.slider.density.help", "↑ Mechanical · Mild λ increase")}
            color="#FFFFFF"
            testId="slider-density"
          />
        </div>

        {/* Performance scores */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="score-bars">
          <div className="rd-sim-card rd-score-card p-3">
            <div className="flex items-center gap-1 mb-2"><Zap className="w-3.5 h-3.5 text-[var(--neon-cyan)]"/>
              <span className="rd-score-title tech-label">{t("rd.sim.score.thermal", "Thermal Performance")}</span>
            </div>
            <Bar value={pred.thermalScore} label="THERMAL" color="#00E5FF"/>
          </div>
          <div className="rd-sim-card rd-score-card p-3">
            <div className="flex items-center gap-1 mb-2"><Flame className="w-3.5 h-3.5 text-[#FF3B30]"/>
              <span className="rd-score-title tech-label">{t("rd.sim.score.fire", "Fire Resistance")}</span>
            </div>
            <Bar value={pred.fireScore} label="FIRE" color="#FF3B30"/>
          </div>
          <div className="rd-sim-card rd-score-card p-3">
            <div className="flex items-center gap-1 mb-2"><Activity className="w-3.5 h-3.5 text-[var(--neon-green)]"/>
              <span className="rd-score-title tech-label">{t("rd.sim.score.mech", "Mechanical Strength")}</span>
            </div>
            <Bar value={pred.mechanicalScore} label="MECHANICAL" color="#39FF14"/>
          </div>
        </div>

        {/* Comparison table */}
        <div className="rd-sim-card" data-testid="comparison-table">
          <div className="rd-sim-table-header grid grid-cols-12 gap-2 px-3 py-2 tech-label">
            <div className="col-span-4">{t("rd.sim.tbl.param", "PARAMETER")}</div>
            <div className="col-span-3">{t("rd.sim.tbl.base", "BASE (EXPERIMENTAL)")}</div>
            <div className="col-span-3">{t("rd.sim.tbl.pred", "PREDICTED")}</div>
            <div className="col-span-2 text-right">{t("rd.sim.tbl.diff", "Δ")}</div>
          </div>
          <ComparePair label="λ (W/mK)"          base={base.lambda.toFixed(4)} pred={pred.lambda}  unit=""    betterIsLower accent="#00E5FF"/>
          <ComparePair label="LOI (%)"           base={base.loi}               pred={pred.loi}     unit=""    accent="#FF3B30"/>
          <ComparePair label="UL94"              base={base.ul94}              pred={pred.ul94}    unit=""    accent={ratingColor(pred.ul94)}/>
          <ComparePair label="pHRR (kW/m²)"      base={base.pHRR}              pred={pred.pHRR}    unit=""    betterIsLower accent="#FF3B30"/>
          <ComparePair label={t("rd.sim.tbl.flexural", "Flexural (kPa)")} base={base.flexural}     pred={pred.flexural} unit=""   accent="#39FF14"/>
          <ComparePair label={t("rd.sim.tbl.tensile",  "Tensile (kPa)")}  base={base.tensile}      pred={pred.tensile} unit=""    accent="#39FF14"/>
          <ComparePair label={t("rd.sim.tbl.compress", "Compressive (kPa)")} base={base.compressive} pred={pred.compressive} unit="" accent="#39FF14"/>
          <ComparePair label={t("rd.sim.tbl.emod",     "E-Modulus (MPa)")} base={base.eModulus}    pred={pred.eModulus} unit=""   accent="#FFFFFF"/>
        </div>

        {/* Optimal Insight */}
        <div data-testid="optimal-insight" className="opt-mix-card">
          <div className="flex items-center gap-2 mb-2 px-3 py-2 -mx-3 -mt-3" style={{ backgroundColor: '#E1AD01', borderRadius: '6px 6px 0 0' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#000000' }}/>
            <span className="opt-mix-title font-mono text-xs uppercase tracking-[0.2em]" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 700, opacity: 1 }}>
              {t("rd.sim.insight.title", "Optimal Mix Insight")}
            </span>
          </div>
          <p className="opt-mix-body text-sm leading-relaxed" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 500, opacity: 1 }}>
            {pred.fireScore >= 60 && pred.thermalScore >= 60
              ? t("rd.sim.insight.balanced", "The current formulation balances thermal and fire performance. Suitable for façade ETICS systems. ")
              : pred.thermalScore >= 70
              ? t("rd.sim.insight.thermal", "High thermal performance achieved — fire resistance is below target. Evaluate against façade height and fire regulations. ")
              : pred.fireScore >= 70
              ? t("rd.sim.insight.fire", "Outstanding fire resistance — thermal performance is moderate. Ideal for high-rise structures or escape corridors. ")
              : t("rd.sim.insight.below", "Mix is below target. Try the 'Balanced' or 'Max Fire' preset.")}
            <br/>
            <span className="opt-mix-hint font-mono text-[11px]" style={{ color: '#000000', WebkitTextFillColor: '#000000', fontWeight: 500, opacity: 1 }}>
              {t("rd.sim.insight.hint", "For target U<0.30 W/m²K, λ ≈ 0.030 + 5 cm thickness is recommended; LOI ≥ 28 for fire.")}
            </span>
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-[10px]">
            <div className="opt-mix-stat" style={{ backgroundColor: '#FFFFFF', border: '2px solid #E1AD01', borderRadius: '6px', padding: '8px 10px' }}>
              <span className="opt-mix-stat-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>{t("rd.sim.insight.graphite", "GRAPHITE")}</span><br/>
              <span className="opt-mix-stat-val text-sm" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 800, opacity: 1 }}>{params.graphite}%</span>
            </div>
            <div className="opt-mix-stat" style={{ backgroundColor: '#FFFFFF', border: '2px solid #E1AD01', borderRadius: '6px', padding: '8px 10px' }}>
              <span className="opt-mix-stat-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>{t("rd.sim.insight.fr", "FLAME RET.")}</span><br/>
              <span className="opt-mix-stat-val text-sm" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 800, opacity: 1 }}>{params.flameRet}%</span>
            </div>
            <div className="opt-mix-stat" style={{ backgroundColor: '#FFFFFF', border: '2px solid #E1AD01', borderRadius: '6px', padding: '8px 10px' }}>
              <span className="opt-mix-stat-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>{t("rd.sim.insight.fiber", "FIBRE")}</span><br/>
              <span className="opt-mix-stat-val text-sm" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 800, opacity: 1 }}>{params.fiber}%</span>
            </div>
            <div className="opt-mix-stat" style={{ backgroundColor: '#FFFFFF', border: '2px solid #E1AD01', borderRadius: '6px', padding: '8px 10px' }}>
              <span className="opt-mix-stat-label" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 700, opacity: 1 }}>{t("rd.sim.insight.densityDelta", "DENSITY Δ")}</span><br/>
              <span className="opt-mix-stat-val text-sm" style={{ color: '#E1AD01', WebkitTextFillColor: '#E1AD01', fontWeight: 800, opacity: 1 }}>{params.densityDelta > 0 ? "+" : ""}{params.densityDelta}%</span>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border border-[#FFCC00] bg-[#1F1A05] p-3 flex items-start gap-2" data-testid="rd-disclaimer">
          <AlertTriangle className="w-4 h-4 text-[#FFCC00] flex-shrink-0 mt-0.5"/>
          <p className="font-mono text-[11px] leading-relaxed text-[#FFE680]">
            <strong>{t("rd.sim.warn.label", "WARNING · Predicted Values:")}</strong>{" "}
            {t("rd.sim.warn.body", "These values are predictions derived from academic research models (Schartel 2010 · Wilkie 2010 · Pielichowska 2014). Before any production decision, laboratory verification (ISO 5660-1, ASTM D3801, TS EN 12089) is mandatory. Data are intended for formulation guidance only and do not guarantee legal/regulatory compliance.")}
          </p>
        </div>
      </div>
    </section>
  );
}

function SliderRow({ label, value, min, max, step, onChange, help, color, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-xs uppercase tracking-wider rd-data-text" style={{ fontWeight: 700, opacity: 1 }}>
          {label}
        </span>
        <span className="font-mono text-sm rd-data-text" style={{ fontWeight: 800, opacity: 1 }}>
          {value.toFixed(step < 1 ? 1 : 0)}{label.includes("%") ? "" : ""}
        </span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0])}
        className="my-2"
      />
      <div className="flex items-center justify-between font-mono text-[10px] rd-data-text" style={{ fontWeight: 600, opacity: 1 }}>
        <span className="rd-data-text">{min} → {max}</span>
        <span className="rd-data-text">{help}</span>
      </div>
    </div>
  );
}
